import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  CatalogSlug,
  DraftId,
  errors,
  PresetId,
  PresetVersionId,
  ProfileSummary,
  paxRoute,
  Timestamp,
  UserId,
} from "../../common";
import {
  automodUnavailable,
  moderationRefused,
  surfaceBanned,
} from "../../moderation/v1alpha1/endpoints";
import { ItemModeration } from "../../moderation/v1alpha1/schemas";
import { PresetCategory } from "./categories";
import { noSuchPreset, teamRefusals } from "./refusals";
import { PresetSlugParams, PresetTags, VersionNumber } from "./schemas";
import { PresetPermission } from "./team";

/** Slugs a preset can't take, because routes beside `/{slug}` use them. */
export const RESERVED_PRESET_SLUGS = ["mine", "drafts", "versions", "artwork", "tags"] as const;

/** What players know a preset as, the same on every version. */
/** The most characters a preset's title runs to: enough to name it, short enough to show whole. */
export const PRESET_TITLE_MAX_CHARACTERS = 60;

export const PresetTitle = z
  .string()
  .trim()
  .min(1)
  .max(PRESET_TITLE_MAX_CHARACTERS)
  .meta({ id: "PresetTitle" });

/**
 * What a draft's page will say once it's published. The API keeps it on the draft's row; while the
 * draft is open, it's also the draft's `page:main` record, which its session writes through.
 */
export const DraftPage = z
  .strictObject({
    name: z.string().max(80).meta({
      description:
        "What its authors call the draft, such as Winter balance pass: up to 80 characters, or empty for none.",
    }),
    changeLog: z.string().max(20_000),
    description: z.string().max(20_000),
    // An update that clears a field takes it out of the record, so a page without one has none.
    category: PresetCategory.nullable().default(null),
    tags: PresetTags,
    allowEarlyCopy: z.boolean(),
  })
  .meta({ id: "DraftPage", description: "What a draft's page will say once it's published." });
export type DraftPage = z.infer<typeof DraftPage>;

/** Something wrong with a draft, at the record it's about when it's about one. */
export const DraftProblem = z
  .strictObject({
    message: z.string().min(1),
    key: z.string().max(240).nullable().meta({ description: "The record it's about, if any." }),
  })
  .meta({ id: "DraftProblem" });
export type DraftProblem = z.infer<typeof DraftProblem>;

/** Where a draft is: being edited, being published, refused by moderation, or appealing that. */
export const DraftStatus = z
  .enum(["draft", "publishing", "refused", "in_appeal"])
  .meta({ id: "DraftStatus" });
export type DraftStatus = z.infer<typeof DraftStatus>;

export const DraftSummary = z
  .strictObject({
    id: DraftId,
    versionNumber: VersionNumber,
    name: z.string(),
    status: DraftStatus.meta({
      description:
        "`publishing` takes no changes, and `in_appeal` takes none until its appeal is decided.",
    }),
    problems: z.array(DraftProblem).meta({
      description: "Why its last publish failed or was refused, or empty.",
    }),
    createdFrom: z
      .strictObject({
        versionId: PresetVersionId,
        version: z.number().int().positive(),
        versionNumber: VersionNumber,
        name: z.string().meta({ description: "That version's name, or empty when it has none." }),
        presetId: PresetId.meta({
          description:
            "The preset that version belongs to: this one, or the one it was copied from.",
        }),
        presetTitle: z.string().meta({ description: "That preset's title." }),
      })
      .nullable()
      .meta({ description: "The version it began from, or null for one begun blank." }),
    createdBy: UserId.nullable(),
    createdAt: Timestamp,
    updatedAt: Timestamp.meta({ description: "When its page last changed." }),
  })
  .meta({ id: "DraftSummary", description: "One draft, as the version menu lists it." });
export type DraftSummary = z.infer<typeof DraftSummary>;

export const VersionSummary = z
  .strictObject({
    id: PresetVersionId,
    version: z.number().int().positive(),
    versionNumber: VersionNumber,
    name: z.string(),
    changeLog: z.string(),
    createdFromVersionId: PresetVersionId.nullable(),
    createdAt: Timestamp,
    publishedAt: Timestamp,
    gamesStarted: z.number().int().nonnegative(),
  })
  .meta({
    id: "VersionSummary",
    description: "One published version, as the version menu lists it.",
  });
export type VersionSummary = z.infer<typeof VersionSummary>;

export const PresetEditing = z
  .strictObject({
    preset: z.strictObject({
      id: PresetId,
      slug: CatalogSlug,
      title: PresetTitle,
      ownerId: UserId,
    }),
    you: z.strictObject({
      userId: UserId,
      owner: z.boolean(),
      rank: z.number().int().nonnegative().nullable().meta({
        description: "Your highest role's rank, or null for the owner, who outranks everyone.",
      }),
      permissions: z.array(PresetPermission),
    }),
    liveVersionId: PresetVersionId.nullable().meta({
      description: "The version new games start on, or null before any is published.",
    }),
    primaryDraftId: DraftId.nullable().meta({
      description: "The draft the team opens first, or null to open the one edited last.",
    }),
    drafts: z.array(DraftSummary).meta({ description: "Edited last first." }),
    versions: z.array(VersionSummary).meta({ description: "Newest first." }),
    moderation: ItemModeration.meta({
      description:
        "Whether moderation refused the preset's latest attempt to publish or invite, as it stands for you, and its latest appeal, which anyone the refusal counted against can file while their roles on the team let them appeal. A preset has one appeal open at a time.",
    }),
  })
  .meta({ id: "PresetEditing", description: "What the editor needs to open a preset." });
export type PresetEditing = z.infer<typeof PresetEditing>;

export const MyPreset = z
  .strictObject({
    id: PresetId,
    slug: CatalogSlug,
    title: z.string(),
    owner: z.boolean(),
    liveVersion: z.number().int().positive().nullable().meta({
      description: "Its live version's number, or null before any is published.",
    }),
    draftCount: z.number().int().nonnegative(),
    editedAt: Timestamp.meta({ description: "When any of its drafts' pages last changed." }),
  })
  .meta({ id: "MyPreset", description: "A preset you own or are on the team of." });
export type MyPreset = z.infer<typeof MyPreset>;

export const PresetInvitation = z
  .strictObject({
    preset: z.strictObject({ id: PresetId, slug: CatalogSlug, title: z.string() }),
    invitedBy: ProfileSummary.nullable(),
    invitedAt: Timestamp,
    roles: z.array(z.string()).meta({ description: "The names of the roles it comes with." }),
  })
  .meta({ id: "PresetInvitation", description: "An invitation to a preset's team, waiting." });
export type PresetInvitation = z.infer<typeof PresetInvitation>;

export const ListMyPresetsResponse = z
  .strictObject({ presets: z.array(MyPreset), invitations: z.array(PresetInvitation) })
  .meta({ id: "ListMyPresetsResponse" });
export type ListMyPresetsResponse = z.infer<typeof ListMyPresetsResponse>;

const Beginning = z
  .strictObject({ versionId: PresetVersionId })
  .nullable()
  .meta({ description: "The published version it begins from, or null to begin blank." });

export const CreatePresetRequest = z
  .strictObject({
    slug: CatalogSlug.refine(
      slug => !(RESERVED_PRESET_SLUGS as readonly string[]).includes(slug),
      "that slug is reserved",
    ),
    title: PresetTitle,
    from: Beginning,
    versionNumber: VersionNumber,
  })
  .meta({
    id: "CreatePresetRequest",
    description: "A new preset, and where its first draft begins and what it's numbered.",
  });
export type CreatePresetRequest = z.infer<typeof CreatePresetRequest>;

export const CreatePresetResponse = z
  .strictObject({
    preset: z.strictObject({ id: PresetId, slug: CatalogSlug }),
    draftId: DraftId,
  })
  .meta({ id: "CreatePresetResponse" });
export type CreatePresetResponse = z.infer<typeof CreatePresetResponse>;

export const BeginDraftRequest = z
  .strictObject({ from: Beginning, versionNumber: VersionNumber })
  .meta({
    id: "BeginDraftRequest",
    description: "Where a new draft of the preset begins, and what it's numbered.",
  });
export type BeginDraftRequest = z.infer<typeof BeginDraftRequest>;

export const DraftParams = z
  .strictObject({ draftId: DraftId })
  .meta({ id: "DraftParams", description: "A route parameter naming one draft." });
export type DraftParams = z.infer<typeof DraftParams>;

export const SetDraftVersionNumberRequest = z
  .strictObject({ versionNumber: VersionNumber })
  .meta({ id: "SetDraftVersionNumberRequest" });
export type SetDraftVersionNumberRequest = z.infer<typeof SetDraftVersionNumberRequest>;

export const SetPrimaryDraftRequest = z
  .strictObject({ draftId: DraftId.nullable() })
  .meta({ id: "SetPrimaryDraftRequest", description: "Null opens the draft edited last." });
export type SetPrimaryDraftRequest = z.infer<typeof SetPrimaryDraftRequest>;

export const SetPresetTitleRequest = z
  .strictObject({ title: PresetTitle })
  .meta({ id: "SetPresetTitleRequest" });
export type SetPresetTitleRequest = z.infer<typeof SetPresetTitleRequest>;

export const SetLiveVersionRequest = z
  .strictObject({ versionId: PresetVersionId })
  .meta({ id: "SetLiveVersionRequest" });
export type SetLiveVersionRequest = z.infer<typeof SetLiveVersionRequest>;

/** How an attempt to publish a draft ended. */
export const PublishOutcome = z.enum(["published", "refused", "failed"]).meta({
  id: "PublishOutcome",
  description:
    "`published`: it's the preset's newest version. `refused`: moderation turned it down. `failed`: it went back to being a draft, with its problems.",
});
export type PublishOutcome = z.infer<typeof PublishOutcome>;

export const PublishDraftResponse = z
  .strictObject({
    outcome: PublishOutcome,
    problems: z.array(DraftProblem),
    version: z
      .strictObject({
        id: PresetVersionId,
        version: z.number().int().positive(),
        versionNumber: VersionNumber,
      })
      .nullable()
      .meta({ description: "The version it became, when it was published." }),
  })
  .meta({ id: "PublishDraftResponse", description: "How the attempt to publish it ended." });
export type PublishDraftResponse = z.infer<typeof PublishDraftResponse>;

export const create = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1",
      operationId: "presets.create",
      summary: "Create a preset.",
      description:
        "Makes a preset you own, with the default roles and every one of its first shares, and its first draft: begun blank, or from a published version you may copy.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The preset and its first draft.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    ...surfaceBanned,
    NOT_FOUND: { message: "That version doesn't exist." },
    CONFLICT: { message: "That slug is taken." },
    TOO_MANY_REQUESTS: { message: "Too many presets at once; try again shortly." },
  })
  .input(z.object({ body: CreatePresetRequest }))
  .output(CreatePresetResponse);

export const listMine = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1/mine",
      operationId: "presets.mine.list",
      summary: "List the presets you work on.",
      description:
        "Every preset you own or have joined the team of, edited last first, and the invitations waiting for you.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...teamRefusals })
  .output(ListMyPresetsResponse);

export const getEditing = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1/{slug}/editing",
      operationId: "presets.editing.get",
      summary: "Open a preset to edit.",
      description:
        "What the editor needs: what your roles let you do, and the preset's drafts and published versions, the newest of which is live.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...teamRefusals, ...noSuchPreset })
  .input(z.object({ params: PresetSlugParams }))
  .output(PresetEditing);

export const beginDraft = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1/{slug}/drafts",
      operationId: "presets.drafts.create",
      summary: "Begin a draft.",
      description:
        "Begins a draft of the preset, blank or from one of its published versions, whose page and artwork it copies. Takes `createDrafts`.",
      inputStructure: "detailed",
      successStatus: 201,
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    ...noSuchPreset,
    ...surfaceBanned,
  })
  .input(z.object({ params: PresetSlugParams, body: BeginDraftRequest }))
  .output(z.strictObject({ draft: DraftSummary }));

export const discardDraft = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/presets/v1alpha1/drafts/{draftId}",
      operationId: "presets.drafts.delete",
      summary: "Discard a draft.",
      description:
        "Deletes the draft and its session. Takes `discardDrafts`, and is refused while it's being published or appealed.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    NOT_FOUND: { message: "The draft doesn't exist, or you aren't on its preset's team." },
    CONFLICT: { message: "It's being published or appealed." },
  })
  .input(z.object({ params: DraftParams }))
  .output(z.strictObject({}));

export const setDraftVersionNumber = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/presets/v1alpha1/drafts/{draftId}/version-number",
      operationId: "presets.drafts.versionNumber.set",
      summary: "Renumber a draft.",
      description:
        "Changes the number the draft goes by. Takes `editOverview`, and is refused while it's being published or when another of the preset's versions already has that number.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    NOT_FOUND: { message: "The draft doesn't exist, or you aren't on its preset's team." },
    CONFLICT: { message: "Another version already has that number, or it's being published." },
  })
  .input(z.object({ params: DraftParams, body: SetDraftVersionNumberRequest }))
  .output(z.strictObject({}));

export const setPrimaryDraft = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/presets/v1alpha1/{slug}/primary-draft",
      operationId: "presets.primaryDraft.set",
      summary: "Choose the draft the team opens first.",
      description: "Takes `choosePrimary`.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...teamRefusals, ...noSuchPreset })
  .input(z.object({ params: PresetSlugParams, body: SetPrimaryDraftRequest }))
  .output(z.strictObject({}));

export const setTitle = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/presets/v1alpha1/{slug}/title",
      operationId: "presets.title.set",
      summary: "Rename a preset.",
      description:
        "Renames the preset on every version, once automod passes the new title. A title automod refuses, or can't check, leaves the old one, and the route answers `MODERATION_REFUSED` or `AUTOMOD_UNAVAILABLE`. Takes `renamePreset`.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    ...noSuchPreset,
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
  })
  .input(z.object({ params: PresetSlugParams, body: SetPresetTitleRequest }))
  .output(z.strictObject({}));

export const setLiveVersion = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/presets/v1alpha1/{slug}/live-version",
      operationId: "presets.liveVersion.set",
      summary: "Choose the version new games start on.",
      description:
        "Moves the preset's live version to one of its published versions, which the catalog shows and new games start on. Takes `chooseLive`. Games already running stay on theirs.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...teamRefusals, ...noSuchPreset })
  .input(z.object({ params: PresetSlugParams, body: SetLiveVersionRequest }))
  .output(z.strictObject({}));

export const publishDraft = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1/drafts/{draftId}/publish",
      operationId: "presets.drafts.publish",
      summary: "Publish a draft.",
      description:
        "Moves the draft to `publishing` and freezes it, has its session compile it, validates the World artifact and moderates the preset as a whole, then publishes it in place as the next version, which goes live, or thaws it with the problems found. A preset automod refuses becomes `refused`, one it can't check goes back to `draft`, and the route answers `MODERATION_REFUSED` or `AUTOMOD_UNAVAILABLE`. Takes `publish`.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
    NOT_FOUND: { message: "The draft doesn't exist, or you aren't on its preset's team." },
    CONFLICT: { message: "It's being published already, or it isn't a draft." },
  })
  .input(z.object({ params: DraftParams }))
  .output(PublishDraftResponse);
